import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { ArrowRight, Loader2 } from "lucide-react";
import ComplianceFooter from "../components/ComplianceFooter";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "", consent_regolamento: false });
  const [submitting, setSubmitting] = useState(false);

  const handle = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm({ ...form, [k]: v });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent_regolamento) {
      toast.error("Devi accettare il regolamento per registrarti");
      return;
    }
    setSubmitting(true);
    const res = await register(form);
    setSubmitting(false);
    if (res.ok) {
      toast.success("Account creato con successo!");
      navigate("/dashboard");
    } else {
      toast.error(res.error);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F7F8F9] p-6" data-testid="register-page">
      <div className="w-full max-w-lg fade-up">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-[#0F7B85]">
          ← Torna alla home
        </Link>
        <div className="mt-6 bg-white border border-slate-200 rounded-2xl p-8 sm:p-10">
          <div className="flex items-center gap-4">
            <img
              src="/brand/besafe-logo.png"
              alt="Besafe Broker"
              style={{ height: "56px", width: "auto", maxWidth: "140px", objectFit: "contain" }}
            />
            <div className="border-l border-slate-200 pl-4">
              <div className="text-xs uppercase tracking-[0.2em] text-slate-500">Crea il tuo account</div>
              <h1 className="font-display text-xl text-[#0F7B85] mt-0.5">Inizia a guadagnare abbuoni</h1>
            </div>
          </div>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="register-form">
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Nome e cognome</label>
              <input
                type="text" required value={form.name} onChange={handle("name")}
                data-testid="register-name-input"
                className="w-full bg-white border border-slate-200 rounded-lg px-4 py-3 focus:ring-2 focus:ring-[#0F7B85] focus:border-transparent outline-none transition-all"
                placeholder="Mario Rossi"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Email</label>
                <input
                  type="email" required value={form.email} onChange={handle("email")}
                  data-testid="register-email-input"
                  className="w-full bg-white border border-slate-200 rounded-lg px-4 py-3 focus:ring-2 focus:ring-[#0F7B85] focus:border-transparent outline-none transition-all"
                  placeholder="nome@email.it"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Cellulare (opzionale)</label>
                <input
                  type="tel" value={form.phone} onChange={handle("phone")}
                  data-testid="register-phone-input"
                  className="w-full bg-white border border-slate-200 rounded-lg px-4 py-3 focus:ring-2 focus:ring-[#0F7B85] focus:border-transparent outline-none transition-all"
                  placeholder="+39 333 1234567"
                />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Password</label>
              <input
                type="password" required value={form.password} onChange={handle("password")}
                data-testid="register-password-input"
                className="w-full bg-white border border-slate-200 rounded-lg px-4 py-3 focus:ring-2 focus:ring-[#0F7B85] focus:border-transparent outline-none transition-all"
                placeholder="Almeno 8 caratteri, lettere e numeri"
                minLength={8}
              />
            </div>
            <label
              className="flex items-start gap-3 bg-[#F7F8F9] border border-slate-200 rounded-xl p-4 cursor-pointer hover:border-[#0F7B85] transition-colors"
              data-testid="register-consent-label"
            >
              <input
                type="checkbox"
                checked={form.consent_regolamento}
                onChange={handle("consent_regolamento")}
                data-testid="register-consent-checkbox"
                required
                className="mt-1 w-4 h-4 rounded border-slate-300 text-[#0F7B85] focus:ring-[#0F7B85]"
              />
              <span className="text-sm text-slate-700 leading-relaxed">
                Dichiaro di aver letto e accettato il{" "}
                <a
                  href={`${process.env.REACT_APP_BACKEND_URL}/api/regolamento.pdf`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#0F7B85] font-semibold hover:underline"
                  data-testid="register-consent-link"
                >
                  regolamento ufficiale &ldquo;Besafe ti premia&rdquo;
                </a>
                {" "}e il trattamento dei dati ai sensi del GDPR (Reg. UE 2016/679).
              </span>
            </label>
            <button
              type="submit" disabled={submitting} data-testid="register-submit-button"
              className="w-full bg-[#0F7B85] text-white hover:bg-[#0A5C64] disabled:opacity-60 rounded-lg px-6 py-3 font-medium transition-colors inline-flex items-center justify-center gap-2"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin"/> : <ArrowRight className="w-4 h-4" />}
              Crea account
            </button>
          </form>

          <div className="my-6 flex items-center gap-3 text-xs uppercase tracking-[0.2em] text-slate-400">
            <div className="flex-1 h-px bg-slate-200" /> oppure <div className="flex-1 h-px bg-slate-200" />
          </div>

          <button
            type="button"
            data-testid="register-google-button"
            onClick={() => {
              // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
              const redirectUrl = window.location.origin + "/dashboard";
              window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
            }}
            className="w-full border border-slate-200 bg-white rounded-lg px-6 py-3 font-medium text-slate-700 hover:bg-slate-50 transition-colors inline-flex items-center justify-center gap-2"
          >
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.71v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.61z" fill="#4285F4"/>
              <path d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.81.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.32A8.99 8.99 0 0 0 9 18z" fill="#34A853"/>
              <path d="M3.97 10.71a5.4 5.4 0 0 1 0-3.42V4.97H.96a9 9 0 0 0 0 8.06l3.01-2.32z" fill="#FBBC05"/>
              <path d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58A8.97 8.97 0 0 0 9 0 8.99 8.99 0 0 0 .96 4.97l3.01 2.32C4.68 5.16 6.66 3.58 9 3.58z" fill="#EA4335"/>
            </svg>
            Continua con Google
          </button>

          <p className="mt-6 text-sm text-center text-slate-600">
            Hai già un account?{" "}
            <Link to="/login" data-testid="link-to-login" className="text-[#0F7B85] font-medium hover:text-[#F39C12]">
              Accedi
            </Link>
          </p>
          <p className="mt-3 text-xs text-center text-slate-500">
            Il tuo consenso al regolamento viene registrato con data e ora ai fini legali (GDPR).
          </p>
          <div className="mt-6 pt-5 border-t border-slate-200 text-[10px] text-slate-400 leading-relaxed text-center">
            BeSafe Broker s.r.l. · Via Emerico Amari, 8 – Palermo · P.IVA <span className="font-mono">07093890825</span><br/>
            Iscritta alla Sezione B del RUI n. <strong>B000725320</strong> · Vigilanza IVASS · <a href="https://servizi.ivass.it" target="_blank" rel="noopener noreferrer" className="text-[#0F7B85] hover:underline">www.servizi.ivass.it</a>
          </div>
        </div>
      </div>
      <ComplianceFooter />
    </div>
  );
}
